import { useMemo, useRef } from 'react';
import { RotateCcw, Calendar, Lock, LockOpen } from 'lucide-react';
import { CompanyGroupFilters } from '@/components/layout/CompanyGroupFilters';
import { UnitModelFilter } from '@/components/layout/UnitModelFilter';
import { IndustryFilter } from '@/components/layout/IndustryFilter';
import { OnboardingTooltip } from '@/components/ui/onboarding-tooltip';
import { LiveDemoToggle } from '@/components/dashboard/LiveDemoToggle';
import { currentSupabaseEnv } from '@/lib/supabase';
import { useLiveDemoContext } from '@/contexts/LiveDemoContext';
import { useLabels } from '@/hooks/useLabels';
import { useFilterOnboarding } from '@/hooks/useFilterOnboarding';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select';
import { useFilters } from '@/contexts/FilterContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import type { EnergyFilters } from '@/types/energy';
import { DATE_RANGE_OPTIONS } from '@/types/energy';

interface EnergyStatusBarProps {
  dateRange: EnergyFilters['dateRange'];
  onDateRangeChange: (value: EnergyFilters['dateRange']) => void;
  onResetFilters: () => void;
  dateRangeLocked?: boolean;
  lastUpdated?: Date | null;
  /** When true, enables onboarding tooltips for empty filters */
  contentLoaded?: boolean;
}

// Format last updated timestamp
function formatLastUpdated(timestamp: Date | string | null | undefined): string {
  if (!timestamp) return 'Never';
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Format date for display (e.g., "Aug 15" or "Dec 20")
function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00'); // Ensure local timezone
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function EnergyStatusBar({
  dateRange,
  onDateRangeChange,
  onResetFilters,
  dateRangeLocked = false,
  lastUpdated,
  contentLoaded = false,
}: EnergyStatusBarProps) {
  const isMobile = useIsMobile();
  const { isLiveDemoEnabled, setLiveDemoEnabled } = useLiveDemoContext();
  const { getLabel } = useLabels();
  const groupLabel = getLabel('group');
  const {
    selectedCompanyIds,
    selectedGroupIds,
    selectedModels,
    companies,
    groups,
  } = useFilters();

  // Refs for filter buttons (for tooltip positioning)
  const industryButtonRef = useRef<HTMLButtonElement>(null);
  const companyButtonRef = useRef<HTMLButtonElement>(null);
  const groupButtonRef = useRef<HTMLButtonElement>(null);
  const modelButtonRef = useRef<HTMLButtonElement>(null);

  // Onboarding state for filter tooltips
  const {
    showIndustryOnboarding,
    showCompanyOnboarding,
    showGroupOnboarding,
    showModelOnboarding,
    industryMessage,
    companyMessage,
    groupMessage,
    modelMessage,
    dismissIndustry,
    dismissCompany,
    dismissGroup,
    dismissModel,
  } = useFilterOnboarding({ contentLoaded });

  // Check if a single parent group with dates is selected (for showing production dates option)
  // This works regardless of whether child sites are also selected
  const productionDateInfo = useMemo(() => {
    // Get selected parent groups (groups without a parent_group_id)
    const selectedParentGroups = groups.filter(
      g => selectedGroupIds.includes(g.id) && !g.parent_group_id
    );

    // Only show if exactly one parent group is selected
    if (selectedParentGroups.length !== 1) return null;

    const selectedGroup = selectedParentGroups[0];
    if (!selectedGroup?.start_date) return null;

    const startFormatted = formatDateShort(selectedGroup.start_date);
    const endFormatted = selectedGroup.end_date ? formatDateShort(selectedGroup.end_date) : 'Present';

    return {
      groupName: selectedGroup.name,
      startDate: selectedGroup.start_date,
      endDate: selectedGroup.end_date,
      label: `${groupLabel} Dates`,
      dateLabel: `${startFormatted} – ${endFormatted}`,
      shortLabel: `${startFormatted}–${endFormatted}`,
    };
  }, [selectedGroupIds, groups, groupLabel]);

  // Check if filters are at default (all selected)
  const hasActiveFilters =
    dateRange !== '30d' ||
    selectedCompanyIds.length !== companies.length ||
    selectedGroupIds.length !== groups.length ||
    selectedModels.length !== 3; // Default is all 3 model types

  // Mobile layout
  if (isMobile) {
    return (
      <>
        <div className="h-12 bg-card border-b border-border flex items-center justify-between px-3 gap-2 shrink-0">
          {/* Left: Filters */}
          <div className="flex items-center gap-1.5">
            <IndustryFilter ref={industryButtonRef} highlight={showIndustryOnboarding} />
            <CompanyGroupFilters
              companyButtonRef={companyButtonRef}
              groupButtonRef={groupButtonRef}
              highlightCompany={showCompanyOnboarding}
              highlightGroup={showGroupOnboarding}
            />
            <UnitModelFilter ref={modelButtonRef} highlight={showModelOnboarding || selectedModels.length === 0} />
          </div>

        {/* Right: Date Range + Reset */}
        <div className="flex items-center gap-1.5">
          {dateRangeLocked ? (
            <button
              onClick={() => setLiveDemoEnabled(false)}
              className="group relative flex items-center justify-between h-8 px-2 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors cursor-pointer w-[90px]"
            >
              <Lock className="h-3 w-3 text-[#8FB560] shrink-0 group-hover:hidden" />
              <LockOpen className="h-3 w-3 text-[#8FB560] shrink-0 hidden group-hover:block" />
              <span className="absolute inset-0 flex items-center justify-center group-hover:hidden">Today</span>
              <span className="absolute inset-0 flex items-center justify-center hidden group-hover:flex text-[#8FB560] font-medium text-[10px]">Disable</span>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-50 shrink-0"><path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
            </button>
          ) : (
            <Select
              value={dateRange}
              onValueChange={(value) => onDateRangeChange(value as EnergyFilters['dateRange'])}
            >
              <SelectTrigger className="h-8 text-xs w-[90px]">
                <Calendar className="h-3 w-3 mr-1" />
                {dateRange === 'production' ? (
                  <span className="truncate">{productionDateInfo?.shortLabel || 'Prod'}</span>
                ) : (
                  <SelectValue placeholder="Range" />
                )}
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.shortLabel}
                  </SelectItem>
                ))}
                {productionDateInfo ? (
                  <>
                    <SelectSeparator />
                    <div className="px-2 py-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                      {productionDateInfo.label}
                    </div>
                    <SelectItem value="production" className="text-[#8FB560]">
                      {productionDateInfo.shortLabel}
                    </SelectItem>
                  </>
                ) : (
                  <SelectItem value="production" className="hidden">Prod</SelectItem>
                )}
              </SelectContent>
            </Select>
          )}
          {hasActiveFilters && (
            <button
              onClick={onResetFilters}
              className="flex items-center justify-center rounded hover:bg-accent transition-colors h-9 w-9 shrink-0"
              title="Reset filters"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
          )}
        </div>
        </div>

        {/* Onboarding Tooltips - rendered via portal */}
        <OnboardingTooltip
          show={showIndustryOnboarding}
          message={industryMessage}
          targetRef={industryButtonRef}
          onDismiss={dismissIndustry}
        />
        <OnboardingTooltip
          show={showCompanyOnboarding}
          message={companyMessage}
          targetRef={companyButtonRef}
          onDismiss={dismissCompany}
        />
        <OnboardingTooltip
          show={showGroupOnboarding}
          message={groupMessage}
          targetRef={groupButtonRef}
          onDismiss={dismissGroup}
        />
        <OnboardingTooltip
          show={showModelOnboarding}
          message={modelMessage}
          targetRef={modelButtonRef}
          onDismiss={dismissModel}
        />
      </>
    );
  }

  // Desktop layout - header + last updated left, filters center, live toggle + date range right
  return (
    <>
      <div className="h-12 bg-card border-b border-border flex items-center justify-between px-6 shrink-0 relative">
        {/* Left: Header + Last Updated - fixed width to balance with right side */}
        <div className="flex items-baseline gap-6 shrink-0 min-w-[320px]">
          <h1 className="text-lg font-bold tracking-tight whitespace-nowrap">Ecoluxe Command Center</h1>
          {lastUpdated && (
            <p className="text-[10px] text-muted-foreground/50 whitespace-nowrap">
              Last Updated {formatLastUpdated(lastUpdated)}
            </p>
          )}
        </div>

        {/* Center: Filter Group + Reset - absolutely centered */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2">
          {/* Filter Group */}
          <div className="flex items-center gap-2">
            <IndustryFilter ref={industryButtonRef} highlight={showIndustryOnboarding} />
            <CompanyGroupFilters
              companyButtonRef={companyButtonRef}
              groupButtonRef={groupButtonRef}
              highlightCompany={showCompanyOnboarding}
              highlightGroup={showGroupOnboarding}
            />
            <UnitModelFilter ref={modelButtonRef} highlight={showModelOnboarding || selectedModels.length === 0} />
          </div>

          {/* Reset Button - right of filter group, always visible but disabled when default */}
          <button
            onClick={onResetFilters}
            disabled={!hasActiveFilters}
            className="flex items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Reset filters"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
        </div>

      {/* Right: Live Toggle + Date Range. The Live toggle drives
          useLiveDemo's SIMULATED fluctuations — demo environment only;
          prod must never present invented data as live. */}
      <div className="flex items-center gap-3 shrink-0">
        {currentSupabaseEnv === 'demo' && (
          <LiveDemoToggle
            enabled={isLiveDemoEnabled}
            onChange={setLiveDemoEnabled}
            variant="switch"
          />
        )}
        {dateRangeLocked ? (
          <button
            onClick={() => setLiveDemoEnabled(false)}
            className="group relative flex items-center justify-between h-8 px-3 rounded-md border border-input bg-background text-xs hover:bg-accent transition-colors cursor-pointer w-[180px]"
          >
            <Lock className="h-3 w-3 text-[#8FB560] shrink-0 group-hover:hidden" />
            <LockOpen className="h-3 w-3 text-[#8FB560] shrink-0 hidden group-hover:block" />
            <span className="absolute inset-0 flex items-center justify-center group-hover:hidden">Today</span>
            <span className="absolute inset-0 flex items-center justify-center hidden group-hover:flex text-[#8FB560] font-medium">Disable Live</span>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 opacity-50 shrink-0"><path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
          </button>
        ) : (
          <Select
            value={dateRange}
            onValueChange={(value) => onDateRangeChange(value as EnergyFilters['dateRange'])}
          >
            <SelectTrigger className="h-8 w-[180px] text-xs">
              <Calendar className="h-3 w-3 mr-1.5 shrink-0" />
              {dateRange === 'production' ? (
                <span className="truncate">{productionDateInfo?.dateLabel || 'Production Dates'}</span>
              ) : (
                <SelectValue placeholder="Date Range" />
              )}
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
              {/* Always include production item for display, but only show in menu when productionDateInfo exists */}
              {productionDateInfo ? (
                <>
                  <SelectSeparator />
                  <div className="px-2 py-1 text-[10px] text-muted-foreground font-medium uppercase tracking-wide">
                    {productionDateInfo.label}
                  </div>
                  <SelectItem value="production" className="text-[#8FB560]">
                    {productionDateInfo.dateLabel}
                  </SelectItem>
                </>
              ) : (
                /* Hidden item so Select can display value when production is selected */
                <SelectItem value="production" className="hidden">
                  Production Dates
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
        </div>
      </div>

      {/* Onboarding Tooltips - rendered via portal */}
      <OnboardingTooltip
        show={showIndustryOnboarding}
        message={industryMessage}
        targetRef={industryButtonRef}
        onDismiss={dismissIndustry}
      />
      <OnboardingTooltip
        show={showCompanyOnboarding}
        message={companyMessage}
        targetRef={companyButtonRef}
        onDismiss={dismissCompany}
      />
      <OnboardingTooltip
        show={showGroupOnboarding}
        message={groupMessage}
        targetRef={groupButtonRef}
        onDismiss={dismissGroup}
      />
      <OnboardingTooltip
        show={showModelOnboarding}
        message={modelMessage}
        targetRef={modelButtonRef}
        onDismiss={dismissModel}
      />
    </>
  );
}
